import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

const DESKTOP_BREAKPOINT = 1024;
const HEADER_OFFSET = 120;
const HEADER_SCROLL_THRESHOLD = 120;
const MENU_FADE_DURATION_MS = 160;
const FIXED_CLASS = "site-header--fixed";
const HIDDEN_CLASS = "site-header--hidden";

gsap.registerPlugin(ScrollToPlugin);

export function initSiteHeader() {
  const header = document.querySelector("[data-site-header]");
  const toggle = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-mobile-menu]");
  if (!header || !toggle || !menu) return;

  const overlay = document.querySelector("[data-menu-overlay]");
  const backgroundRegions = Array.from(document.querySelectorAll("main, .site-footer"));
  const previousInert = new Map();

  const menuLinks = Array.from(menu.querySelectorAll("a"));
  let menuOpen = false;
  let menuCloseTimer;
  let lastScrollY = window.scrollY;
  let anchorFrame;
  let anchorTween;

  const stopAnchorScroll = () => {
    const wasScrollingToAnchor = Boolean(anchorTween);
    window.cancelAnimationFrame(anchorFrame);
    anchorTween?.kill();
    anchorTween = null;
    // Wheel puede llegar después del desplazamiento del compositor y antes
    // de scroll. No perder esa distancia cuando no hay un ancla que cancelar.
    if (wasScrollingToAnchor) lastScrollY = Math.max(window.scrollY, 0);
  };

  // Interrumpir por intención real del usuario. ScrollToPlugin autoKill puede
  // confundir las variaciones del viewport de iOS con un desplazamiento manual.
  window.addEventListener("touchstart", stopAnchorScroll, { passive: true });
  window.addEventListener("wheel", stopAnchorScroll, { passive: true });
  window.addEventListener("keydown", (event) => {
    if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Escape"].includes(event.key)) {
      stopAnchorScroll();
    }
  });

  if (lastScrollY > HEADER_SCROLL_THRESHOLD) {
    header.classList.add(FIXED_CLASS, HIDDEN_CLASS);
  }

  const hideClosedMenu = () => {
    if (!menuOpen) {
      menu.hidden = true;
      if (overlay) overlay.hidden = true;
    }
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    if (!menuOpen) return;

    menuOpen = false;
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Abrir menú");
    menu.classList.remove("mobile-menu--visible");
    document.body.classList.remove("menu-is-open");
    overlay?.classList.remove("mobile-menu-overlay--visible");
    previousInert.forEach((value, region) => { region.inert = value; });
    previousInert.clear();

    window.clearTimeout(menuCloseTimer);
    menuCloseTimer = window.setTimeout(
      hideClosedMenu,
      MENU_FADE_DURATION_MS + 20,
    );

    if (restoreFocus) toggle.focus({ preventScroll: true });
  };

  const openMenu = () => {
    stopAnchorScroll();
    window.clearTimeout(menuCloseTimer);
    menuOpen = true;
    if (window.scrollY > 1) header.classList.add(FIXED_CLASS);
    header.classList.remove(HIDDEN_CLASS);
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Cerrar menú");
    menu.hidden = false;
    if (overlay) {
      overlay.hidden = false;
      overlay.getBoundingClientRect();
      overlay.classList.add("mobile-menu-overlay--visible");
    }
    backgroundRegions.forEach((region) => {
      previousInert.set(region, region.inert);
      region.inert = true;
    });
    menu.getBoundingClientRect();
    menu.classList.add("mobile-menu--visible");
    document.body.classList.add("menu-is-open");
  };

  toggle.addEventListener("click", () => {
    if (menuOpen) closeMenu({ restoreFocus: true });
    else openMenu();
  });

  overlay?.addEventListener("click", () => closeMenu({ restoreFocus: true }));

  menuLinks.forEach((link) => {
    link.addEventListener("click", () => closeMenu());
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && menuOpen) closeMenu({ restoreFocus: true });
    if (event.key === "Tab" && menuOpen) {
      const controls = Array.from(header.querySelectorAll("a, button"))
        .filter((control) => control.getClientRects().length && !control.closest("[hidden]"));
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  });

  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href^='#']");
    if (!link) return;

    const targetId = link.getAttribute("href");
    if (!targetId || targetId === "#") return;

    const target = document.querySelector(targetId);
    if (!target || target.hidden || target.closest("[hidden]")) return;

    event.preventDefault();
    stopAnchorScroll();
    closeMenu();
    gsap.killTweensOf(window);

    // Medir tras retirar el bloqueo de scroll del menú, con el margen que
    // corresponde al header móvil (96px) o al de escritorio (120px).
    anchorFrame = window.requestAnimationFrame(() => {
      const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop)
        || HEADER_OFFSET;
      header.classList.add(FIXED_CLASS);
      header.classList.remove(HIDDEN_CLASS);
      anchorTween = gsap.to(window, {
        duration: 0.65,
        ease: "power2.out",
        scrollTo: {
          y: target,
          offsetY: offset,
          autoKill: false,
        },
        onComplete: () => {
          anchorTween = null;
          lastScrollY = Math.max(window.scrollY, 0);
          if (lastScrollY <= 1) header.classList.remove(FIXED_CLASS, HIDDEN_CLASS);
        },
      });
    });
  });

  window.addEventListener(
    "scroll",
    () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      if (anchorTween) {
        lastScrollY = currentScrollY;
        return;
      }
      // Un evento sin desplazamiento no indica que el usuario esté subiendo.
      if (currentScrollY === lastScrollY) return;
      const scrollingDown = currentScrollY > lastScrollY;

      if (currentScrollY <= 1) {
        header.classList.remove(FIXED_CLASS, HIDDEN_CLASS);
      } else if (!menuOpen && scrollingDown && currentScrollY > HEADER_SCROLL_THRESHOLD) {
        header.classList.add(HIDDEN_CLASS);
      } else if (!scrollingDown) {
        const becomingFixed = !header.classList.contains(FIXED_CLASS);
        header.classList.add(FIXED_CLASS);

        if (becomingFixed && header.classList.contains(HIDDEN_CLASS)) {
          header.getBoundingClientRect();
          window.requestAnimationFrame(() => header.classList.remove(HIDDEN_CLASS));
        } else {
          header.classList.remove(HIDDEN_CLASS);
        }
      }

      lastScrollY = currentScrollY;
    },
    { passive: true },
  );

  window.addEventListener("resize", () => {
    if (window.innerWidth >= DESKTOP_BREAKPOINT) closeMenu();
  });
}
